import { ExportImage, type Pixel } from "@pdfking/renderer-api"
import type { PdfiumApi, PdfiumTypeMap } from "@pdfking/renderer-wasm-pdfium"
import type { Bytes } from "@pdfking/tfunc"

type PointerBitmap = PdfiumTypeMap["FPDF_BITMAP"]
type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]
type PointerPage = PdfiumTypeMap["FPDF_PAGE"]

export const RenderPage = async (
  { call, safeCall, readBytes }: PdfiumApi,
  doc_pointer: PointerDoc,
  page: number,
  {
    scale = 2,
    max_size = 1980,
  }: {
    scale?: number
    max_size?: number
  } = {}
) => {
  let page_pointer: null | PointerPage = null
  let bitmap_pointer: null | PointerBitmap = null

  try {
    page_pointer = await safeCall("FPDF_LoadPage", doc_pointer, page - 1)

    const width = await call("FPDF_GetPageWidthF", page_pointer)
    const height = await call("FPDF_GetPageHeightF", page_pointer)

    let scaled_width = Math.floor(width * scale)
    let scaled_height = Math.floor(height * scale)

    if (max_size) {
      const max_dimension = Math.max(scaled_width, scaled_height)
      if (max_dimension > max_size) {
        const scale_factor = max_size / max_dimension
        scaled_width = Math.floor(scaled_width * scale_factor)
        scaled_height = Math.floor(scaled_height * scale_factor)
      }
    }

    bitmap_pointer = await safeCall(
      "FPDFBitmap_Create",
      scaled_width,
      scaled_height,
      1
    )

    await call(
      "FPDF_RenderPageBitmap",
      bitmap_pointer,
      page_pointer,
      0,
      0,
      scaled_width,
      scaled_height,
      0,
      16 // FPDF_REVERSE_BYTE_ORDER
    )

    const bufferPtr = await safeCall("FPDFBitmap_GetBuffer", bitmap_pointer)

    const buffer = readBytes(
      bufferPtr,
      (scaled_width * scaled_height * 4) as Bytes
    )

    const image_data = new ImageData(
      new Uint8ClampedArray(buffer.buffer as ArrayBuffer),
      scaled_width,
      scaled_height
    )

    const blob = await ExportImage(image_data)

    return {
      stream: [blob],
      size: {
        width: scaled_width as Pixel,
        height: scaled_height as Pixel,
      },
    }
  } finally {
    if (bitmap_pointer) await call("FPDFBitmap_Destroy", bitmap_pointer)
    if (page_pointer) await call("FPDF_ClosePage", page_pointer)
  }
}
